import { ArrowLeftIcon } from '@heroicons/react/outline';
import { joiResolver } from '@hookform/resolvers/joi';
import bankSVG from 'assets/images/svgs/bank.svg';
import BankIcon from 'assets/svg/icons/bankIcon';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import { Loader } from 'components/atoms/Loader';
import type { ModalApi } from 'components/atoms/Modal';
import { Modal } from 'components/atoms/Modal';
import SectionSeparator from 'components/atoms/SectionSeparator.component';
import SimpleAlertV2 from 'components/atoms/SimpleAlertV2.component';
import SimpleError from 'components/molecules/SimpleError.component';
import SearchBar from 'components/organisms/Filters/SearchBar';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCreateBankConnection from 'hooks/Bank/useCreateBankConnection';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import useUpdateBankConnection from 'hooks/Bank/useUpdateBankConnection';
import { AnimatePresence, motion } from 'motion/react';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from 'react-query';
import type { Bank, BankProviders } from 'types/banks.types';
import { BankFieldType, BankStatus } from 'types/banks.types';
import useVividBlockedModal from './useVividBlockedModal';
import { CurrenciesEnum, type QP } from 'types/global.types';
import type { Connector } from 'types/transactions.types';
import { getTracingEvents } from 'utils/amplitude';
import {
  WEBAPP_BANKSLIST_TINKMODAL_CLICKEDIBANINPUT,
  WEBAPP_BANKSLIST_TINKMODAL_CLICKEDSELECTYOURBANKSTEP,
  WEBAPP_BANKSLIST_TINKMODAL_CLICKEDSUBMIT,
  WEBAPP_BANKSLIST_TINKMODAL_SELECTEDBANK,
} from 'utils/amplitude/events/bank.amplitude';
import { removeSoftHyphens } from 'utils/helpers';
import {
  ArrowLeftIconOutline,
  ChevronRightIcon,
  UserIconOutline,
} from 'utils/icons';
import useBanksList from '../useBanksList';
import {
  checkMatchingConnector,
  getFormData,
  isUserWithLessProminentProfession,
} from './helpers';
import useCanAccessAccountableBanking from 'hooks/Bank/useCanAccessAccountableBanking';
import { isEmpty } from 'lodash';
import useWalletOnboardingModal from '../WalletOnboarding/useWalletOnboardingModal';
import useHasAccountableBanking from 'hooks/Bank/useHasAccountableBanking';
import accountableBankingLogo from 'assets/svg/icons/accoutableConnect/A_aldo.svg';

const tracingEvents = getTracingEvents({
  onSelectBank: {
    name: WEBAPP_BANKSLIST_TINKMODAL_SELECTEDBANK,
    data: (bank: string) => ({ bank }),
  },
  onClickSelectBankStep: WEBAPP_BANKSLIST_TINKMODAL_CLICKEDSELECTYOURBANKSTEP,
});

type TinkBankSelectorProps = {
  bank?: Bank;
  bankSlug?: string;
  group?: string;
  successQP?: { numberOfConnectors: number } & QP;
  renderTrigger?: (props: ModalApi) => ReactNode;
  currentConnector?: Connector;
};

const transition = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

type BankFormMainFieldName = 'accountReference' | 'email' | 'iban';

type BankFormFields = {
  bankId: string | number;
  provider: BankProviders;
  accountCurrency: CurrenciesEnum;
} & {
  [K in BankFormMainFieldName]?: string;
};

const Form: FC<{
  bank: Bank;
  successQP: { numberOfConnectors: number } & QP;
  afterSubmit: () => void;
  connectors: Connector[];
  currentConnector?: Connector;
}> = ({ bank, successQP, afterSubmit, connectors, currentConnector }) => {
  const { t } = useTranslation();

  const updateConnectorAPI = useUpdateBankConnection(
    currentConnector as Connector,
  );

  const userEmail = useGetCurrentCustomerData().data?.email as string;

  const createApi = useCreateBankConnection(successQP);

  const alreadyHasConnectionWithThisBank =
    connectors?.find(
      (connector) =>
        connector.bankAccountReference?.financialInstitutionSlug ===
        bank?.financialInstitutionSlug,
    ) && !currentConnector;

  const formData = useMemo(() => {
    const data = getFormData({
      fields: bank?.fields,
      email: userEmail,
      IBAN: currentConnector?.bankAccountReference.IBAN,
    });

    if (bank.status === BankStatus.dev) {
      return { ...data, validationSchema: undefined };
    }
    return data;
  }, [
    bank?.fields,
    bank.status,
    userEmail,
    currentConnector?.bankAccountReference.IBAN,
  ]);

  const formApi = useForm<BankFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      bankId: bank.id,
      provider: bank.provider,
      ...formData.defaultValues,
    },
    resolver: formData.validationSchema
      ? joiResolver(formData.validationSchema, { allowUnknown: true })
      : undefined,
  });

  const { isSubmitting } = formApi.formState;

  const accountReferenceValue =
    formApi.watch(formData.field.name as BankFormMainFieldName) || '';

  const accountCurrencyValue = formApi.watch('accountCurrency') || '';

  const hasMatchingConnector = checkMatchingConnector(
    connectors,
    accountReferenceValue,
    accountCurrencyValue,
  );

  return (
    <>
      <img src={bankSVG} className="h-[48px]" />
      <h3 className="text-primary my-2 text-2xl font-semibold">
        {t('banks.bankPicker.step2.title')}
      </h3>
      <div className="text-center text-primary-600 ">
        <p className="font-semibold ">
          {formData.field.bankFieldType === BankFieldType.iban
            ? t('banks.bankPicker.methods.iban.title')
            : t('banks.bankPicker.methods.email.title')}
        </p>
        <p>{t('banks.bankPicker.methods.subTitle')}</p>
      </div>
      <FormProvider {...formApi}>
        <form
          className="w-full my-8"
          onSubmit={formApi.handleSubmit((values) => {
            if (currentConnector?._id) {
              return updateConnectorAPI
                .mutateAsync({
                  bankId: values.bankId as string,
                  financialInstitutionSlug: bank.financialInstitutionSlug,
                })
                .then(afterSubmit);
            }

            return createApi
              .mutateAsync({
                ...values,
                financialInstitutionSlug: bank.financialInstitutionSlug,
              })
              .then(afterSubmit);
          })}
        >
          <FieldConnector name="accountReference">
            {({ field: { onChange, ...field } }) => (
              <FreeTextInput
                {...field}
                autoFocus
                placeholder={
                  formData.field.bankFieldType === BankFieldType.iban
                    ? t('banks.bankPicker.fields.iban.label')
                    : t('banks.bankPicker.fields.email.label')
                }
                Icon={
                  formData.field.bankFieldType === BankFieldType.iban
                    ? BankIcon
                    : UserIconOutline
                }
                type={formData.field.type}
                onChange={({ target: { value } }) => {
                  const _value = value || '';
                  onChange(_value);
                }}
                tracing={{
                  onFocus: WEBAPP_BANKSLIST_TINKMODAL_CLICKEDIBANINPUT,
                }}
              />
            )}
          </FieldConnector>

          <FieldConnector
            name="accountCurrency"
            label={t('invoice.field.currency')}
            className="mt-3"
          >
            {({ field: { onChange, ...rest } }) => {
              const currencies = Object.keys(CurrenciesEnum).map(
                (currencyCode) => ({
                  label: removeSoftHyphens(t(`currency.${currencyCode}`)),
                  value: currencyCode,
                }),
              );

              return (
                <Select
                  isSearchable
                  placeholder={t('invoice.field.select')}
                  options={currencies}
                  onChange={(option) => {
                    onChange(option.value);
                  }}
                  {...rest}
                />
              );
            }}
          </FieldConnector>

          {alreadyHasConnectionWithThisBank ? (
            <div className="mt-4">
              <SimpleAlertV2
                type="warning"
                subtitle={t(
                  'banks.bankPicker.already_have_connection_alert.content',
                )}
              />
            </div>
          ) : null}

          {hasMatchingConnector ? (
            <div className="mt-4">
              <SimpleAlertV2
                type="warning"
                subtitle={t('banks.bankPicker.has_matching_connector.content')}
              />
            </div>
          ) : null}

          <div className="text-center">
            <Button
              type="submit"
              loading={isSubmitting}
              // size="lg"
              className="mt-8"
              tracingEvent={WEBAPP_BANKSLIST_TINKMODAL_CLICKEDSUBMIT}
            >
              {t('continue')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

const Content: FC<
  TinkBankSelectorProps & {
    afterSubmit: () => void;
    banksGrouped: Record<string, Bank[]>;
    mostCommonBanks: [string, Bank[]][];
    allOtherBanks: [string, Bank[]][];
    banksListQuery: UseQueryResult<Bank[]>;
  }
> = (props) => {
  const { t } = useTranslation();

  const preselectedBankBySlug = (props.banksListQuery.data || []).find(
    (bank) => bank.financialInstitutionSlug === props.bankSlug,
  );

  const { data } = useGetConnectors();

  const connectors = data as Connector[];
  const hasConnectorsData = Array.isArray(connectors) && !isEmpty(connectors);
  const [selectedBank, setSelectedBank] = useState(
    preselectedBankBySlug || props.bank,
  );

  const [selectedGroup, setSelectedGroup] = useState(props.group || '');

  const [mainBanksSearchValue, setMainBanksSearchValue] = useState('');

  const [searchValue, setSearchValue] = useState('');

  const selectedGroupBanks = props.banksGrouped?.[selectedGroup];

  const groupFirstBank = selectedGroupBanks?.[0];

  const mostCommonBanksWithSearch = props.mostCommonBanks.filter(([key]) =>
    key?.toLocaleLowerCase()?.includes(mainBanksSearchValue.toLowerCase()),
  );

  const allOtherBanksWithSearch = props.allOtherBanks.filter(([key]) =>
    key?.toLocaleLowerCase()?.includes(mainBanksSearchValue?.toLowerCase()),
  );
  const canAccessAccountableBanking = useCanAccessAccountableBanking();
  const [vividBlockedModal, vividBlockedModalAPI] = useVividBlockedModal(
    canAccessAccountableBanking,
    hasConnectorsData,
  );

  const { data: user } = useGetCurrentCustomerData();
  const isLessProminentProfession = isUserWithLessProminentProfession(user);
  const hasAccountableBanking = useHasAccountableBanking();

  const shouldShowAccountableBankingCard =
    !hasAccountableBanking &&
    canAccessAccountableBanking &&
    isLessProminentProfession;
  const [walletOnboardingModal, walletOnboardingModalAPI] =
    useWalletOnboardingModal();

  return (
    <div className="pl-2">
      <AnimatePresence mode="wait">
        {selectedGroup && !selectedBank ? (
          <motion.div key="group_details" {...transition}>
            <Button
              asDefault
              onClick={() => setSelectedGroup('')}
              className="flex items-center space-x-2 pl-3"
            >
              <ArrowLeftIcon className="w-4 h-4 text-primary-800" />

              <span className="text-sm text-primary-700 font-semibold">
                {t('back')}
              </span>
            </Button>

            <div className="flex items-center space-x-3 mt-8 mb-3 border-b border-b-taxes-100 pb-3 pl-3">
              {groupFirstBank.logoUrl ? (
                <img
                  src={groupFirstBank.logoUrl}
                  className="w-10 h-10 object-contain"
                />
              ) : null}

              <span className="font-semibold">{selectedGroup}</span>
            </div>

            <SearchBar
              className="mb-2"
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              placeholder={t('banks.bankPicker.search_branches_placeholder')}
            />

            <div className="grid grid-cols-2 gap-4">
              {selectedGroupBanks
                .filter((bank: Bank) =>
                  bank.financialInstitutionName
                    ?.toLocaleLowerCase()
                    .includes(searchValue?.toLocaleLowerCase()),
                )
                .map((bank: Bank) => (
                  <Button
                    className={classNames(
                      'flex items-center border border-primary-100 rounded-lg w-full text-sm px-3 font-semibold text-primary-900 py-5',
                      'hover:bg-primary-25 focus:bg-primary-25',
                    )}
                    key={bank.financialInstitutionSlug}
                    asDefault
                    onClick={() => {
                      tracingEvents.onSelectBank(bank.name);
                      setSelectedBank(bank);
                    }}
                  >
                    <ChevronRightIcon className="text-bank w-4" />

                    <span className="group-hover:pl-3 transition-all text-sm text-left">
                      {bank.financialInstitutionName}
                    </span>
                  </Button>
                ))}
            </div>
          </motion.div>
        ) : null}

        {!selectedBank && !selectedGroup ? (
          <motion.div key="groups" {...transition}>
            <div className="flex flex-col justify-center items-center mb-4 gap-2">
              <img src={bankSVG} className="h-[48px]" />
              <h3 className="text-primary-600 my-2 text-2xl font-semibold">
                {t('banks.bankPicker.step1.title')}
              </h3>
            </div>
            <SearchBar
              className="mb-6"
              searchValue={mainBanksSearchValue}
              setSearchValue={setMainBanksSearchValue}
              placeholder={t('banks.bankPicker.search_banks_placeholder')}
            />
            <motion.div
              key="data-1"
              {...transition}
              className="max-h-[60vh] pr-4 overflow-y-auto force-scroll-style"
            >
              {props.bankSlug && !preselectedBankBySlug ? (
                <div className="mb-4">
                  <SimpleAlertV2
                    type="warning"
                    subtitle={t(
                      'banks.bankPicker.couldnt_preselect_bank_alert.content',
                    )}
                  />
                </div>
              ) : null}

              {mostCommonBanksWithSearch.length > 0 ? (
                <div>
                  <h1 className="text-sm text-primary-600 mb-4">
                    {t('banks.bankPicker.most_common.title')}
                  </h1>
                  <div className="grid grid-cols-2 gap-4">
                    {mostCommonBanksWithSearch.map(([key, banks]) => {
                      // Check if this is the Vivid bank
                      const isVividBank = banks[0].id === 'vivid-hardcoded';

                      const firstBank = banks[0];

                      const isGrouped = banks.length > 1;

                      return (
                        <Button
                          key={key}
                          asDefault
                          onClick={() => {
                            if (isVividBank) {
                              return vividBlockedModalAPI.open();
                            }

                            if (isGrouped) {
                              return setSelectedGroup(key);
                            }

                            tracingEvents.onSelectBank(firstBank.name);
                            setSelectedBank(firstBank);
                          }}
                          className={classNames(
                            'flex flex-col items-center border border-primary-100 rounded-lg w-full text-sm font-semibold gap-3 text-primary-900 py-5',
                            'hover:bg-primary-25 focus:bg-primary-25',
                          )}
                        >
                          {firstBank.logoUrl ? (
                            <img
                              src={firstBank.logoUrl}
                              className="w-10 h-10 object-contain"
                            />
                          ) : null}

                          <span className="text-primary-600">{key}</span>
                        </Button>
                      );
                    })}
                  </div>

                  <SectionSeparator className="my-2 mb-5" />
                </div>
              ) : null}

              {allOtherBanksWithSearch.length > 0 ? (
                <div>
                  <h1 className="text-sm text-primary-600 mb-4">
                    {t('banks.bankPicker.other_banks.title')}
                  </h1>
                  <div className="grid grid-cols-2 gap-4">
                    {shouldShowAccountableBankingCard ? (
                      <Button
                        asDefault
                        onClick={() => {
                          walletOnboardingModalAPI.open();
                        }}
                        className={classNames(
                          'flex flex-col items-center border border-primary-100 rounded-lg w-full text-sm font-semibold gap-3 text-primary-900 py-5',
                          'hover:bg-primary-25 focus:bg-primary-25',
                        )}
                      >
                        <img
                          src={accountableBankingLogo}
                          className="w-10 h-10 object-contain"
                          alt="Accountable Banking onboarding"
                        />
                        <span className="text-primary-600">
                          Accountable Banking
                        </span>
                      </Button>
                    ) : null}
                    {allOtherBanksWithSearch.map(([key, banks]) => {
                      const firstBank = banks[0];
                      const isGrouped = banks.length > 1;

                      return (
                        <Button
                          key={key}
                          asDefault
                          onClick={() => {
                            if (isGrouped) {
                              return setSelectedGroup(key);
                            }

                            tracingEvents.onSelectBank(firstBank.name);
                            setSelectedBank(firstBank);
                          }}
                          className={classNames(
                            'flex flex-col items-center border border-primary-100 rounded-lg w-full text-sm px-3 font-semibold gap-3 text-primary-900 py-5',
                            'hover:bg-primary-25 focus:bg-primary-25',
                          )}
                        >
                          {firstBank.logoUrl ? (
                            <img
                              src={firstBank.logoUrl}
                              className="w-10 h-10 object-contain"
                            />
                          ) : null}

                          <span className="text-primary-600">{key}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </motion.div>
          </motion.div>
        ) : null}

        {!!selectedBank && (
          <motion.div key="bank_form" {...transition}>
            <div className="w-full flex">
              <Button
                structure="text"
                onClick={() => setSelectedBank(undefined)}
              >
                <ArrowLeftIconOutline className="text-light w-6 h-6" />
              </Button>
            </div>
            <motion.div
              key="data-2"
              {...transition}
              className="flex flex-col items-center justify-center px-2"
            >
              <Form
                bank={selectedBank}
                successQP={props.successQP || { numberOfConnectors: 0 }}
                afterSubmit={props.afterSubmit}
                connectors={connectors}
                currentConnector={props.currentConnector}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="flex mt-4 justify-center items-center gap-2"
        key="dots"
        {...transition}
      >
        <div
          className={classNames('w-2 h-2 bg-bank-100 rounded-full', {
            'bg-bank': !selectedBank,
          })}
        />
        <div
          className={classNames('w-2 h-2 bg-bank-100 rounded-full', {
            'bg-bank': !!selectedBank,
          })}
        />
      </motion.div>
      {vividBlockedModal}
      {walletOnboardingModal}
    </div>
  );
};

const TinkBankSelector: FC<
  TinkBankSelectorProps & {
    modalRef?: React.RefObject<ModalApi>;
  }
> = (props) => {
  const { banksListQuery, banksGrouped, mostCommonBanks, allOtherBanks } =
    useBanksList();

  const { isLoading, isError, refetch } = banksListQuery;

  return (
    <Modal
      name="Tink banks selector"
      className="!max-w-[600px]"
      trigger={props.renderTrigger || (() => null)}
      apiRef={props.modalRef}
      render={(api) =>
        isLoading ? (
          <motion.div
            key="loading"
            {...transition}
            className="min-h-[400px] flex justify-center items-center"
          >
            <Loader size="3xl" />
          </motion.div>
        ) : isError ? (
          <motion.div
            key="error"
            {...transition}
            className="min-h-[400px] flex justify-center items-center"
          >
            <SimpleError
              messageClassName="!text-xl"
              onRetry={() => refetch()}
            />
          </motion.div>
        ) : (
          <Content
            {...props}
            {...{
              banksGrouped,
              mostCommonBanks,
              allOtherBanks,
              banksListQuery,
            }}
            afterSubmit={api.closeModal}
          />
        )
      }
    />
  );
};

export default TinkBankSelector;
